import { Icon } from '@sunwebgroup/uds-ui/icon'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { sub } from 'date-fns'
import * as React from 'react'
import { useState } from 'react'
import { useIntl } from '@/intl'
import {
  EquipmentsForm,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/Equipments/EquipmentsForm'
import {
  getTotalPriceForEquipmentsSelected,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import {
  CarSelectedSheetCategoryText,
  CarSelectedSheetContent,
  CarSelectedSheetHintText,
  CarSelectedSheetTrailing,
  CarSelectedSheetTrigger,
  CarSelectedSheetTriggerContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import { useCarRentalFilteringServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { parseDateForLocalFormatting } from '@/scopes/common/domain/utils/dates'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './Equipments.module.css'

export function EquipmentsSheet({ openByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingCar, endEditingCar,
  } = useCarRentalFilteringServiceStore()
  const [showEquipmentsForm, setShowEquipmentsForm] = useState(false)
  const { participants, configurations } = service || {}
  const { driverId, value: carValue, id: carId, selectedEquipmentsWithQuantity, equipments } = option
  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })

  const driver = driverId ? participants?.[driverId] : null
  React.useEffect(() => {
    if (openByDefault) {
      setTimeout(() => {
        setShowEquipmentsForm(true)
      }, 300)
    }
  }, [setShowEquipmentsForm, openByDefault])

  const handleClickEquipmentsForm = () => {
    setShowEquipmentsForm(true)
    startEditingCar(carId, {
      driverId: driverId ?? '',
      driverBirthDate: driver?.birthdate ? parseDateForLocalFormatting(driver.birthdate) : new Date(defaultDriverBirthdate),
      driverHasCreditCard: Boolean(driverId && driverId !== '0'),
      bookedEquipmentsWithQuantity: option.selectedEquipmentsWithQuantity?.map(({ id, quantity }) => {
        return {
          id,
          quantity,
        }
      }) ?? [],
      isEditing: Boolean(driverId && driverId !== '0'),
    })
    sendEvent(
      createCarEvent({
        result: 'edit',
        type: carValue,
        description: 'add equipments button clicked',
        element: 'button',
      }),
    )
  }

  const handleEndEditing = () => {
    endEditingCar(option.value)
    setShowEquipmentsForm(false)
  }

  if (!service)
    return null
  if (!option)
    return null
  const selectedEquipmentsList = equipments.filter(({ id }) => selectedEquipmentsWithQuantity?.map(({ id: selectedEquipmentId }) => selectedEquipmentId).includes(id))
  const totalEquipmentPrice = getTotalPriceForEquipmentsSelected(option)

  if (!equipments || equipments.length === 0)
    return null

  return (
    <Sheet.Root
      open={showEquipmentsForm}
      onOpenChange={(open) => {
        if (!open) {
          handleEndEditing()
        }
      }}
    >
      <Sheet.Trigger>
        <CarSelectedSheetTrigger>
          <ListItem variant="tinted">
            <CarSelectedSheetTriggerContent onClick={handleClickEquipmentsForm}>
              <Icon glyph="PlusInSquare" size={32} />
              <CarSelectedSheetContent>
                <CarSelectedSheetCategoryText variant="body-s-regular-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_overline' })}</CarSelectedSheetCategoryText>
                <CarSelectedSheetCategoryText className={cn(styles.extras)} variant="body-m-bold-tight">
                  { (!selectedEquipmentsList || selectedEquipmentsList.length === 0) && (intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_action_title' }))}
                  { (selectedEquipmentsList.length > 0) && (<CarSelectedEquipments equipments={selectedEquipmentsList} />)}
                </CarSelectedSheetCategoryText>

                { (selectedEquipmentsList.length > 0) && (
                  <CarSelectedSheetHintText variant="body-s-regular-tight">
                    { intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_paid_locally' }) }
                  </CarSelectedSheetHintText>
                )}
              </CarSelectedSheetContent>
              <CarSelectedSheetTrailing>
                { (selectedEquipmentsList.length > 0 && totalEquipmentPrice && totalEquipmentPrice > 0)
                  ? (
                      <Typography variant="body-m-bold-tight">{intl.formatCurrency(totalEquipmentPrice)}</Typography>
                    )
                  : null}
                <Icon glyph="ChevronRight" size={24} />
              </CarSelectedSheetTrailing>
            </CarSelectedSheetTriggerContent>
          </ListItem>
        </CarSelectedSheetTrigger>
      </Sheet.Trigger>
      <Sheet.Body side="right" hasPadding bottomSideInMobile>
        <Sheet.Header titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_sheet_title' })} closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_sheet_cancel_text' })} />
        <EquipmentsForm {...option} showEquipmentsForm={showEquipmentsForm} setShowEquipmentsForm={setShowEquipmentsForm} />
      </Sheet.Body>
    </Sheet.Root>
  )
}
function CarSelectedEquipments({ equipments }: { equipments: { id: string, text: string }[] }) {
  if (!equipments.length)
    return null
  return (
    equipments.map(({ text }) => text).join(', ')
  )
}
